import { SphereRadioButton } from "@components/common/sphere-radio-button";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { ValueLabelItem } from "@custom-types/types";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Grid2, RadioGroup } from "@mui/material";
import {
  companyCommunicationSettingsSelector,
  defaultProjectLaunchTargetSelector,
  fetchingSdbCompanyFlagsSelector,
} from "@store/sdb-company/sdb-company-selector";
import { updateCompanyCommunicationSettings } from "@store/sdb-company/sdb-company-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { CommonStyles } from "@styles/common-styles";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

/** The available default platform items */
const DEFAULT_PLATFORM_ITEMS: Record<
  ProjectLaunchTarget,
  ValueLabelItem<ProjectLaunchTarget>
> = {
  [ProjectLaunchTarget.sphereViewer]: {
    value: ProjectLaunchTarget.sphereViewer,
    label: "Sphere XG",
  },
  [ProjectLaunchTarget.webEditor]: {
    value: ProjectLaunchTarget.webEditor,
    label: "HoloBuilder",
  },
};

/** Renders the company setting that allows EAs to select the default platform to open projects */
export function DefaultPlatformSelector(): JSX.Element {
  const { isScreenSmOrSmaller } = useMediaQueryList();
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const projectLaunchSettings = useAppSelector(
    defaultProjectLaunchTargetSelector
  );

  const { isUpdatingCompanyCommunicationSettings } = useAppSelector(
    fetchingSdbCompanyFlagsSelector
  );
  const communicationSettings = useAppSelector(
    companyCommunicationSettingsSelector
  );

  function handlePlatformChange(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    const platform = event.target.value as ProjectLaunchTarget;

    trackEvent({
      name: WorkspaceEvents.updateSettings,
      props: {
        defaultPlatform: DEFAULT_PLATFORM_ITEMS[platform].label as string,
      },
    });

    void dispatch(
      updateCompanyCommunicationSettings({
        payload: {
          // eslint-disable-next-line @typescript-eslint/naming-convention -- name defined in backend
          sphereViewerIsDefault: platform === ProjectLaunchTarget.sphereViewer,
        },
      })
    );
  }

  return (
    <RadioGroup
      value={projectLaunchSettings.defaultOpenProjectTarget}
      onChange={handlePlatformChange}
      sx={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        height: "100%",
      }}
    >
      {Object.values(DEFAULT_PLATFORM_ITEMS).map((item, index) => (
        <Grid2
          alignItems="center"
          alignContent="center"
          key={item.value}
          sx={{
            // Show 2 rows for smaller screens
            width: isScreenSmOrSmaller ? "100%" : "50%",
            // Don't show the left border if its the 1st item or if 2 rows are shown
            borderLeft:
              index === 0 || isScreenSmOrSmaller
                ? "none"
                : CommonStyles.Borders.gray200Divider,
          }}
        >
          <SphereRadioButton
            item={item}
            // Disable the radio buttons if:
            // - The settings is being updated
            // - Fetching the setting failed and is not available in the store
            isDisabled={
              isUpdatingCompanyCommunicationSettings || !communicationSettings
            }
            formControlLabelSx={{
              marginLeft: isScreenSmOrSmaller ? "0px" : "20px",
            }}
          />
        </Grid2>
      ))}
    </RadioGroup>
  );
}
